/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { PDFDocument, PDFTextField, PDFDropdown, PDFCheckBox } from 'pdf-lib';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import DateInput from '../components/DateInput';
import FileInput, { FileInputHandle } from '../components/FileInput';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import EmploymentDocumentsService from '../../../../../services/EmploymentDocuments';
import { documentsOptions } from '../../../../../helpers/documentsOptions';
import { i9EnglishFields } from '../../../../../helpers/i9EnglishFields';
import { i9SpanishFields } from '../../../../../helpers/i9SpanishFields';
import { getPreEmploymentLookup, PreEmploymentLookup } from '../utils/preEmploymentLookup';
import { buildI9Pdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { BaseStepProps } from './BaseStepProps';

interface UploadedItem {
  id: string;
  documentType: string;
  fileName?: string;
}

const formatDateForPdf = (raw: string): string => {
  if (!raw) return '';
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return raw;
};

const getI9DefaultFromLookup = (index: number, lookup: PreEmploymentLookup): string => {
  switch (index) {
    case 98:
      return lookup.lastName;
    case 5:
      return lookup.firstName;
    case 99:
      return lookup.address;
    case 101:
      return lookup.city;
    case 93:
      return lookup.state;
    case 94:
      return lookup.zipCode;
    case 95:
      return lookup.dob;
    case 96:
      return lookup.ssn;
    case 102:
      return lookup.email;
    case 97:
      return lookup.phone;
    default:
      return '';
  }
};

const I9_ENGLISH_PDF =
  'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/Employment-Eligibility/i-9-english.pdf';
const I9_SPANISH_PDF =
  'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/Employment-Eligibility/i-9-spanish.pdf';

interface I9FieldDef {
  fieldType: string;
  fieldName: string;
  fieldIndex: number;
  fieldMaxLength?: number;
  fieldOptions?: string[];
  fieldReadOnly?: boolean;
}

interface I9FieldValue {
  fieldType: string;
  fieldName: string;
  fieldValue: string;
  fieldIndex: number;
}

interface I9Draft {
  values: I9FieldValue[];
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const formatSsn = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const isFieldVisibleForEmployee = (config: any): boolean => {
  if (!config) return false;
  if (config.onlyForRole && Array.isArray(config.onlyForRole)) return false;
  if (config.type === 'title') return false;
  if (config.type === 'collapse') return false;
  return true;
};

const ENGLISH_GATING_LABELS: Record<number, string> = {
  0: 'USCIS or A-Number',
  103: 'Work-authorized until (mm/dd/yyyy)',
};

const SPANISH_GATING_LABELS: Record<number, string> = {
  113: 'USCIS o Número A',
  13: 'Autorizado para trabajar hasta (mm/dd/aaaa)',
};

interface I9StepBaseProps extends BaseStepProps {
  spanish?: boolean;
}

const I9StepBase = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  uploadedDocuments,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
  spanish,
}: I9StepBaseProps) => {
  const lookup = useMemo(
    () => getPreEmploymentLookup(application, applicationDraft, token),
    [application?.json, applicationDraft?.json, token],
  );
  const pdfUrl = spanish ? I9_SPANISH_PDF : I9_ENGLISH_PDF;
  const fieldsConfig = spanish ? i9SpanishFields : i9EnglishFields;
  const docType = spanish ? 'I9-Spanish' : 'I9';
  const uploadsRequired = (tokenData.employmentEligibilityDocs || []).includes('TWO_DOCS_FOR_PROOF');

  const backendDraft = draftDocs.find((d) => d.type === docType);
  const initialFromBackend: I9Draft = {
    values: (backendDraft?.options?.formFieldsValues as I9FieldValue[]) || [],
  };
  const initial = (draft as I9Draft) || initialFromBackend;
  const [values, setValues] = useState<I9FieldValue[]>(initial.values || []);
  const [formFields, setFormFields] = useState<I9FieldDef[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const [uploadDocumentType, setUploadDocumentType] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadItems, setUploadItems] = useState<UploadedItem[]>(
    (uploadedDocuments || []).map((d: any) => ({
      id: d.id,
      documentType: d.documentType,
      fileName: d.fileName || d.document?.name,
    })),
  );
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const fileInputRef = useRef<FileInputHandle>(null);
  const uploadOptions = documentsOptions.filter((d: any) => d.value);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const arrayBuffer = await fetch(pdfUrl).then((res) => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pdfForm = pdfDoc.getForm();
        const detected: I9FieldDef[] = [];
        const defaults: I9FieldValue[] = [];
        let count = 0;
        for (const f of pdfForm.getFields()) {
          let type = '';
          if (f instanceof PDFTextField) type = 'PDFTextField';
          else if (f instanceof PDFDropdown) type = 'PDFDropdown';
          else if (f instanceof PDFCheckBox) type = 'PDFCheckBox';
          const fieldName = f.getName();
          const cfg = fieldsConfig.find((c: any) => c.i === count);
          detected.push({
            fieldType: type,
            fieldName,
            fieldIndex: count,
            fieldMaxLength: type === 'PDFTextField' ? (f as PDFTextField).getMaxLength() : undefined,
            fieldOptions: type === 'PDFDropdown' ? (f as PDFDropdown).getOptions() : [],
            fieldReadOnly: cfg?.readOnly || false,
          });
          let defaultValue = cfg?.defaultValue || '';
          if (defaultValue === '%schoolName%') defaultValue = tokenData.school?.name || '';
          if (defaultValue === '%schoolAddress%') defaultValue = tokenData.school?.address || '';
          if (!defaultValue) {
            const fromLookup = getI9DefaultFromLookup(count, lookup);
            if (fromLookup) defaultValue = fromLookup;
          }
          defaults.push({ fieldType: type, fieldName, fieldValue: defaultValue, fieldIndex: count });
          count++;
        }
        if (cancelled) return;
        setFormFields(detected);
        setValues((prev) => {
          if (!prev.length) return defaults;
          let changed = false;
          const merged = defaults.map((d) => {
            const existing = prev.find((v) => v.fieldIndex === d.fieldIndex);
            if (existing?.fieldValue) return existing;
            if (existing && existing.fieldValue === d.fieldValue) return existing;
            changed = true;
            return d;
          });
          return changed ? merged : prev;
        });
      } catch (err) {
        console.error('[I9] Failed to load PDF fields', err);
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, fieldsConfig, tokenData.school, lookup]);

  const persistDraft = (nextValues: I9FieldValue[]) => {
    saveDraft({ values: nextValues });
  };

  const updateValue = (fieldIndex: number, value: string) => {
    const next = values.map((v) => (v.fieldIndex === fieldIndex ? { ...v, fieldValue: value } : v));
    setValues(next);
    persistDraft(next);
  };

  const toggleCheckbox = (cfg: any, checked: boolean) => {
    let next = values.map((v) => (v.fieldIndex === cfg.i ? { ...v, fieldValue: checked ? 'true' : '' } : v));
    if (cfg.name) {
      const siblings = fieldsConfig.filter((c: any) => c.name === cfg.name && c.i !== cfg.i);
      for (const sibling of siblings) {
        next = next.map((v) => (v.fieldIndex === sibling.i ? { ...v, fieldValue: '' } : v));
        for (const idx of (sibling.enable as number[] | undefined) || []) {
          next = next.map((v) => (v.fieldIndex === idx ? { ...v, fieldValue: '' } : v));
        }
      }
    }
    if (!checked) {
      for (const idx of (cfg.enable as number[] | undefined) || []) {
        next = next.map((v) => (v.fieldIndex === idx ? { ...v, fieldValue: '' } : v));
      }
    }
    setValues(next);
    persistDraft(next);
  };

  const canAddUpload = !!uploadDocumentType && !!uploadFile && !uploadSubmitting;

  const handleAddUpload = async () => {
    if (!canAddUpload || !uploadFile) return;
    setUploadSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('employeeId', tokenData.employeeId);
      formData.append('documentType', uploadDocumentType);
      formData.append('document', uploadFile);
      const result = await EmploymentDocumentsService(token).createWithFile(formData);
      setUploadItems([
        ...uploadItems,
        {
          id: result?.data?.id || result?.id || `${Date.now()}`,
          documentType: uploadDocumentType,
          fileName: uploadFile.name,
        },
      ]);
      setUploadDocumentType('');
      setUploadFile(null);
      fileInputRef.current?.reset();
      toast.success('Document uploaded.');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not upload document.');
    } finally {
      setUploadSubmitting(false);
    }
  };

  const unlockedFieldIndexes = useMemo(() => {
    const set = new Set<number>();
    for (const cfg of fieldsConfig) {
      if (!cfg.enable) continue;
      const v = values.find((val) => val.fieldIndex === cfg.i)?.fieldValue;
      if (v === 'true') {
        for (const idx of cfg.enable as number[]) set.add(idx);
      }
    }
    return set;
  }, [fieldsConfig, values]);

  const visibleConfigs = useMemo(() => {
    const supplementStart = fieldsConfig.findIndex(
      (c: any) => c.type === 'title' && /supplement|suplemento/i.test(c.label || ''),
    );
    const employeeConfigs = supplementStart >= 0 ? fieldsConfig.slice(0, supplementStart) : fieldsConfig;
    return employeeConfigs.filter((c: any) => isFieldVisibleForEmployee(c));
  }, [fieldsConfig]);

  const requiredFilled = useMemo(() => {
    return visibleConfigs
      .filter((c: any) => c.required)
      .every((c: any) => {
        const v = values.find((val) => val.fieldIndex === c.i)?.fieldValue;
        return v != null && v !== '';
      });
  }, [visibleConfigs, values]);

  const uploadsValid = !uploadsRequired || uploadItems.length > 0;
  const hasPendingUpload = uploadsRequired && (!!uploadFile || !!uploadDocumentType);
  const canOpenSignature = !loadingFields && requiredFilled && uploadsValid && !hasPendingUpload && !savingDraft;

  const valuesForPdf = useMemo(
    () =>
      values.map((v) => {
        const cfg = fieldsConfig.find((c: any) => c.i === v.fieldIndex);
        let fieldValue = v.fieldValue;
        if (cfg?.inputType === 'date') fieldValue = formatDateForPdf(fieldValue);
        else if (cfg?.inputType === 'ssn') fieldValue = (fieldValue || '').replace(/\D/g, '').slice(0, 9);
        return {
          fieldType: v.fieldType as 'PDFTextField' | 'PDFDropdown' | 'PDFCheckBox',
          fieldName: v.fieldName,
          fieldValue,
        };
      }),
    [values, fieldsConfig],
  );

  const livePdfUrl = useLivePdfPreview({
    builder: () => buildI9Pdf({ spanish, fieldValues: valuesForPdf }),
    deps: [valuesForPdf, spanish],
    enabled: !loadingFields,
  });

  const submitWithSignature = async (signaturePng: string) => {
    const pdfBytes = await buildI9Pdf({ spanish, fieldValues: valuesForPdf, signaturePng });
    const pdfSigned = await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' }));

    await HireDocuments('').create({
      type: docType,
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        formFields,
        formFieldsValues: values,
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('I-9 signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      await HireDocuments('').create({
        type: docType,
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: {
          formFields,
          formFieldsValues: values,
          signPng: '',
        },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save I-9.');
    } finally {
      setSavingDraft(false);
    }
  };

  const renderField = (cfg: any) => {
    const value = values.find((v) => v.fieldIndex === cfg.i)?.fieldValue || '';
    const fieldDef = formFields.find((f) => f.fieldIndex === cfg.i);
    const colSpan = cfg.size === '4' ? 'span 1' : cfg.size === '6' ? 'span 1' : 'span 2';
    const isUnlocked = unlockedFieldIndexes.has(cfg.i);
    const isLocked = (cfg.readOnly || fieldDef?.fieldReadOnly) && !isUnlocked;
    if (isLocked && fieldDef?.fieldType !== 'PDFCheckBox') return null;
    const friendlyLabels = spanish ? SPANISH_GATING_LABELS : ENGLISH_GATING_LABELS;
    const fieldLabel = cfg.label || friendlyLabels[cfg.i as number] || '';
    const onChange = (raw: string) => {
      let next = raw;
      if (cfg.inputType === 'ssn') next = formatSsn(raw);
      updateValue(cfg.i, next);
    };

    if (fieldDef?.fieldType === 'PDFCheckBox') {
      const checked = value === 'true';
      return (
        <label
          key={cfg.i}
          style={{
            ...layout.optionCard,
            ...(checked ? layout.optionCardSelected : {}),
            gridColumn: 'span 2',
          }}
        >
          <input type="checkbox" checked={checked} onChange={(e) => toggleCheckbox(cfg, e.target.checked)} />
          {fieldLabel}
        </label>
      );
    }

    if (fieldDef?.fieldType === 'PDFDropdown') {
      return (
        <div key={cfg.i} style={{ gridColumn: colSpan }}>
          <label style={layout.label}>
            {fieldLabel} {cfg.required ? '*' : ''}
          </label>
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={isLocked}
            style={{ ...layout.select, ...(isLocked ? { background: '#E5E7EB' } : {}) }}
          >
            <option value="">Select...</option>
            {fieldDef.fieldOptions?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (cfg.inputType === 'date') {
      return (
        <div key={cfg.i} style={{ gridColumn: colSpan }}>
          <label style={layout.label}>
            {fieldLabel || ' '} {cfg.required ? '*' : ''}
          </label>
          <DateInput value={value} onChange={(v) => onChange(v)} disabled={isLocked} />
        </div>
      );
    }

    const displayValue = cfg.inputType === 'ssn' ? formatSsn(value) : value;

    return (
      <div key={cfg.i} style={{ gridColumn: colSpan }}>
        <label style={layout.label}>
          {fieldLabel || ' '} {cfg.required ? '*' : ''}
        </label>
        <input
          type={cfg.inputType === 'email' ? 'email' : 'text'}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          maxLength={cfg.inputType === 'ssn' ? 11 : fieldDef?.fieldMaxLength}
          readOnly={isLocked}
          placeholder={cfg.inputType === 'ssn' ? 'XXX-XX-XXXX' : undefined}
          style={{
            ...layout.input,
            ...(isLocked ? { background: '#E5E7EB' } : {}),
          }}
        />
      </div>
    );
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
        {spanish
          ? 'Formulario I-9: Verificación de Elegibilidad de Empleo'
          : 'Form I-9: Employment Eligibility Verification'}
      </h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        {spanish
          ? 'Revise el formulario a continuación, complete los campos, suba los documentos requeridos y firme al final.'
          : 'Review the form below, complete the fields, upload required documents and sign at the bottom.'}
      </p>

      <InlinePdfViewer url={livePdfUrl || pdfUrl} maxHeight="55vh" />

      <div style={{ ...layout.infoBox, background: '#FEF3C7', color: '#92400E' }}>
        <i className="fas fa-exclamation-triangle" />
        <span>
          Please bring your original identity and work-authorization documents on your first day. Your director will
          verify them within 3 business days of your start date.
        </span>
      </div>

      {loadingFields ? (
        <p style={{ color: colors.muted }}>Loading form fields...</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 8 }}>
          {visibleConfigs.map((cfg: any) => renderField(cfg))}
        </div>
      )}

      {uploadsRequired && (
        <>
          <h4 style={{ fontSize: 15, fontWeight: 700, margin: '24px 0 6px' }}>
            Upload Identity &amp; Eligibility Documents
          </h4>
          <p style={{ color: colors.muted, fontSize: 13, marginTop: 0 }}>
            Upload at least one document that proves your identity and authorization to work. Acceptable file types:
            PNG, JPG, PDF.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={layout.label}>Document Type *</label>
            <select
              value={uploadDocumentType}
              onChange={(e) => setUploadDocumentType(e.target.value)}
              style={layout.select}
            >
              <option value="">Select document type...</option>
              {uploadOptions.map((opt: any) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={layout.label}>Document File *</label>
            <FileInput ref={fileInputRef} selected={uploadFile} onChange={setUploadFile} hint="PNG, JPG or PDF" />
          </div>

          <div
            style={{
              ...layout.infoBox,
              background: canAddUpload ? '#FEF3C7' : colors.accentSoft,
              color: canAddUpload ? '#92400E' : '#1E3A8A',
              marginBottom: 10,
            }}
          >
            <i className={`fas ${canAddUpload ? 'fa-exclamation-triangle' : 'fa-info-circle'}`} />
            <span>
              {canAddUpload
                ? 'Click "Upload Document" below to add the selected file to your list before signing.'
                : 'Select a document type and a file, then click "Upload Document" below to add it to your list. You can upload more than one.'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleAddUpload}
            disabled={!canAddUpload}
            style={{
              ...(canAddUpload ? layout.primaryBtn : layout.disabledBtn),
              marginBottom: 16,
              width: '100%',
              fontSize: 15,
              padding: '14px 20px',
              ...(canAddUpload ? { boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.18)', animation: 'none' } : {}),
            }}
          >
            <i className="fas fa-upload" style={{ marginRight: 8 }} />
            {uploadSubmitting ? 'Uploading...' : 'Upload Document'}
          </button>

          {uploadItems.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {uploadItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 12,
                    background: '#F9FAFB',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                    marginBottom: 8,
                    fontSize: 13,
                  }}
                >
                  <i className="fas fa-check-circle" style={{ color: colors.success, fontSize: 18 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{item.documentType}</div>
                    {item.fileName && <div style={{ color: colors.muted, fontSize: 12 }}>{item.fileName}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title={spanish ? 'Firmar I-9' : 'Sign I-9'}
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

const I9Step = (props: BaseStepProps) => <I9StepBase {...props} />;

export { I9StepBase };
export default I9Step;
